import React, { useEffect, useState, useCallback } from "react";
import { audio } from "services/audioManager";

import styles from "components/MenuStyles.module.scss";
import ErrorModal from "components/Utility/ErrorModal";

import { GameStates } from "constants/constants";

const LEAVE_ACTIONS = {
    restart: {
        message: "restart_game",
        confirmTitle: "Are you sure you want to restart the game? All progress will be reset.",
        confirmButton: "Restart Game"
    },
    lobby: {
        message: "change_game",
        confirmTitle: "Are you sure you want to return to the Game.City Lobby?",
        confirmButton: "Go To Lobby"
    }
};

export default function Menu({ room, menuOpen, toggleMenu, mutedMusic, mutedSfx, gameState, isMirror }) {

    const [showConfirmLeave, setShowConfirmLeave] = useState(false);
    const [confirmLeaveAction, setConfirmLeaveAction] = useState(null);

    // Mute/unmute music when the prop changes
    useEffect(() => {
        if (mutedMusic === undefined) return;
        audio.muteMusic(mutedMusic);
    }, [mutedMusic]);

    // Mute/unmute sfx + vo when the prop changes
    useEffect(() => {
        if (mutedSfx === undefined) return;
        audio.muteSfx(mutedSfx);
        audio.muteVo(mutedSfx);
    }, [mutedSfx]);

    // Close confirm dialog if the game state changes while it's open
    useEffect(() => {
        if (showConfirmLeave) {
            setShowConfirmLeave(false);
            setConfirmLeaveAction(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState]);

    const handleConfirmLeave = useCallback((action) => {
        setShowConfirmLeave(true);
        setConfirmLeaveAction(action);
    }, []);

    const closeConfirmLeave = useCallback(() => {
        setShowConfirmLeave(false);
        setConfirmLeaveAction(null);
    }, []);

    const doLeaveAction = useCallback(() => {
        const action = LEAVE_ACTIONS[confirmLeaveAction];
        if (action?.message) {
            room.send(action.message, {});
        }
        toggleMenu();
        closeConfirmLeave();
    }, [confirmLeaveAction, room, toggleMenu, closeConfirmLeave]);

    const toggleMuteMusic = useCallback(() => {
        room.send("mute_music", { mute: !mutedMusic });
    }, [room, mutedMusic]);

    const toggleMuteSfx = useCallback(() => {
        room.send("mute_sfx", { mute: !mutedSfx });
    }, [room, mutedSfx]);

    const canRestartGame = gameState !== undefined &&
        ![GameStates.Loading, GameStates.MainMenu, GameStates.GameConfig, GameStates.Tutorial].includes(gameState);

    return (
        <div className={`${styles.menuContainer} ${menuOpen ? styles.open : ""} `}>
            <div className={`${styles.menuSection} ${menuOpen ? styles.open : ""}`}>
                <div className={styles.spacer} />
                <div className={`${styles.button} ${isMirror ? styles.disabled : ''}`} onClick={isMirror ? undefined : toggleMuteMusic}>
                    {mutedMusic ? "Unmute Music" : "Mute Music"}
                </div>
                <div className={styles.spacer} />
                <div className={`${styles.button} ${isMirror ? styles.disabled : ''}`} onClick={isMirror ? undefined : toggleMuteSfx}>
                    {mutedSfx ? "Unmute Sound Effects" : "Mute Sound Effects"}
                </div>
                <div className={styles.spacer} />
                <div className={`${styles.button} ${isMirror ? styles.disabled : ''}`} onClick={isMirror ? undefined : () => handleConfirmLeave("lobby")}>
                    Go To Lobby
                </div>
                <div className={styles.spacer} />
                {canRestartGame && <>
                    <div className={`${styles.button} ${isMirror ? styles.disabled : ''}`} onClick={isMirror ? undefined : () => handleConfirmLeave("restart")}>
                        Restart Game
                    </div>
                    <div className={styles.spacer} />
                </>}
                <div className={styles.button} onClick={toggleMenu}>
                    Close
                </div>
                <div className={styles.spacer} />
            </div>

            {showConfirmLeave && (
                <ErrorModal
                    title="Are you sure?"
                    message={LEAVE_ACTIONS[confirmLeaveAction]?.confirmTitle}
                    callback={closeConfirmLeave}
                    callbackText="Cancel"
                    callback2={doLeaveAction}
                    callbackText2={LEAVE_ACTIONS[confirmLeaveAction]?.confirmButton}
                />
            )}
        </div>
    );
}
